/**
 * DEV Eligibility Injection Layer
 * 
 * DEV/INTERNAL USE ONLY
 * 
 * This layer allows DEV agents with overrides to selectively bypass
 * specific eligibility checks for testing purposes.
 * 
 * CRITICAL RULES:
 * - Each override ONLY bypasses its specific check (partial overrides)
 * - NO override may imply or infer another override
 * - Vertical compatibility is ALWAYS enforced (never bypassed)
 * - This NEVER affects simulations or production traffic
 * - All injections are session-scoped and non-persistent
 * 
 * ELIGIBILITY DIMENSION MAPPING:
 * - Lead Type: bypassed only if forceAcceptAllLeadTypes OR overrideAcceptedLeadTypes includes it
 * - Licensed State: bypassed only if overrideLicensedStates includes the state
 * - Contract Caps: bypassed only if ignoreContractCaps === true
 * - Pricing Rules: bypassed only if ignorePricingRules === true
 * - Wallet: bypassed only if global DEV forceWalletBalance is set
 */

import { isDevMode } from '@/lib/featureFlags';
import type { MockAgent } from '@/contexts/MockAgentsContext';
import type { IncomingLead } from '@/lib/multiAgentRouter';
import { 
  arePerAgentOverridesActive, 
  getAllPerAgentOverrides,
  type PerAgentOverrides,
  DEFAULT_PER_AGENT_OVERRIDES,
} from './perAgentOverrides';
import { areDevOverridesActive, DEV_AGENT_OVERRIDES } from './agentOverrides';
import type { RoutingContextSnapshot } from '@/lib/leadStateMachine';

// --- Types ---

export type EligibilitySource = 'REAL' | 'DEV_INJECTED';

/** Which eligibility checks were bypassed vs enforced */
export interface DevOverrideAttribution {
  leadType: { bypassed: boolean; reason?: string };
  licensedState: { bypassed: boolean; reason?: string };
  contractCaps: { bypassed: boolean; reason?: string };
  pricingRules: { bypassed: boolean; reason?: string };
  wallet: { bypassed: boolean; reason?: string };
}

export interface InjectionMetadata {
  eligibilitySource: EligibilitySource;
  devInjectedAgentIds: string[];
  injectionReason?: string;
  /** Per-agent attribution of which checks were bypassed */
  agentAttributions?: Record<string, DevOverrideAttribution>;
}

export interface InjectedAgent extends MockAgent {
  /** Flag indicating this agent was DEV-injected */
  _devInjected: true;
  /** Attribution of which checks were bypassed for this agent */
  _devOverrideAttribution: DevOverrideAttribution;
}

export interface EligibilityInjectionResult {
  /** The agents to use for routing (may include injected agents) */
  agents: MockAgent[];
  /** Metadata about the injection for observability */
  metadata: InjectionMetadata;
}

// --- Default values for synthetic preferences ---

const SYNTHETIC_PREFERENCE_BID = 50;
const SYNTHETIC_PREFERENCE_DAILY_CAP = 100;
const SYNTHETIC_PREFERENCE_MONTHLY_CAP = 1000;

// --- Core Functions ---

/**
 * Check if DEV eligibility injection is available.
 * Returns true if DEV mode is active AND any agent overrides exist.
 */
export function isDevInjectionAvailable(): boolean {
  if (!isDevMode) return false;
  
  // Check if global DEV overrides are active
  if (areDevOverridesActive()) return true;
  
  // Check if per-agent overrides exist
  if (arePerAgentOverridesActive()) return true;
  
  return false;
}

/**
 * Check if DEV injection is available based on persisted routing context.
 * Used for retries where session state may differ from persisted state.
 */
export function isDevInjectionAvailableFromContext(
  routingContext: RoutingContextSnapshot | undefined
): boolean {
  if (!isDevMode) return false;
  if (!routingContext?.devOverridesEnabled) return false;
  
  return true;
}

/**
 * Get agent IDs that should be DEV-injected based on overrides.
 * An agent is injectable if they have any DEV override enabled.
 */
export function getInjectableAgentIds(): string[] {
  const injectableIds: string[] = [];
  
  // If global overrides force all agents eligible, all agents are injectable
  if (areDevOverridesActive() && DEV_AGENT_OVERRIDES.forceAllAgentsEligible) {
    // This case is handled specially - all agents become injectable
    return ['__ALL__'];
  }
  
  // Otherwise, collect agents with per-agent overrides
  if (arePerAgentOverridesActive()) {
    const overrides = getAllPerAgentOverrides();
    for (const [agentId, override] of overrides) {
      if (hasActiveOverride(override)) {
        injectableIds.push(agentId);
      }
    }
  }
  
  return injectableIds;
}

/**
 * Get injectable agent IDs from persisted routing context.
 * Used for retries to ensure deterministic behavior.
 */
export function getInjectableAgentIdsFromContext(
  routingContext: RoutingContextSnapshot
): string[] {
  // If forceAllAgentsEligible was captured, inject all
  if (routingContext.forceAllAgentsEligible) {
    return ['__ALL__'];
  }
  
  // If there are per-agent overrides captured, use those agent IDs
  if (routingContext.perAgentOverrides?.enabled) {
    return Object.keys(routingContext.perAgentOverrides.agentOverrides);
  }
  
  // If devInjectedAgentIds was captured (from previous injection), use those
  if (routingContext.devInjectedAgentIds && routingContext.devInjectedAgentIds.length > 0) {
    return routingContext.devInjectedAgentIds;
  }
  
  return [];
}

/**
 * Check if an override has any active settings.
 */
function hasActiveOverride(override: PerAgentOverrides): boolean {
  return (
    override.forceAcceptAllLeadTypes ||
    override.overrideAcceptedLeadTypes.length > 0 ||
    override.overrideLicensedStates.length > 0 ||
    override.ignoreContractCaps ||
    override.ignorePricingRules
  );
}

/**
 * Create a DEV-injected agent with PARTIAL overrides only.
 * 
 * CRITICAL: This function only bypasses checks that have EXPLICIT overrides.
 * No override implies another override.
 * 
 * Returns the agent with only the explicitly overridden eligibility aspects modified.
 */
function createInjectedAgent(
  agent: MockAgent, 
  lead: IncomingLead,
  override?: PerAgentOverrides,
  globalOverrides?: {
    forceAllAgentsEligible?: boolean;
    forceWalletBalance?: number;
    forceLicenses?: string[];
  }
): InjectedAgent {
  // Start with a copy of the original agent - do NOT modify anything by default
  const injected: InjectedAgent = {
    ...agent,
    _devInjected: true,
    _devOverrideAttribution: {
      leadType: { bypassed: false },
      licensedState: { bypassed: false },
      contractCaps: { bypassed: false },
      pricingRules: { bypassed: false },
      wallet: { bypassed: false },
    },
    // Deep clone arrays to avoid mutation
    licensedStates: [...agent.licensedStates],
    leadPreferences: agent.leadPreferences 
      ? agent.leadPreferences.map(pref => ({ ...pref }))
      : [],
  };

  const effectiveOverride = override ?? DEFAULT_PER_AGENT_OVERRIDES;
  const attribution = injected._devOverrideAttribution;

  // --- LEAD TYPE OVERRIDE ---
  // Only bypass lead type check if explicitly configured
  let shouldBypassLeadType = false;
  let leadTypeBypassReason = '';
  
  if (globalOverrides?.forceAllAgentsEligible) {
    shouldBypassLeadType = true;
    leadTypeBypassReason = 'Global forceAllAgentsEligible';
  } else if (effectiveOverride.forceAcceptAllLeadTypes) {
    shouldBypassLeadType = true;
    leadTypeBypassReason = 'Per-agent forceAcceptAllLeadTypes';
  } else if (effectiveOverride.overrideAcceptedLeadTypes.includes(lead.leadTypeId)) {
    shouldBypassLeadType = true;
    leadTypeBypassReason = `Lead type ${lead.leadTypeId} in overrideAcceptedLeadTypes`;
  }

  if (shouldBypassLeadType) {
    attribution.leadType = { bypassed: true, reason: leadTypeBypassReason };
    
    // Check if agent has a preference for this lead type
    const hasLeadTypePreference = injected.leadPreferences?.some(
      pref => pref.leadTypeId === lead.leadTypeId
    );
    
    if (!hasLeadTypePreference) {
      // Add synthetic preference for the lead type
      injected.leadPreferences.push({
        leadTypeId: lead.leadTypeId,
        leadTypeName: lead.leadTypeName,
        bidValue: SYNTHETIC_PREFERENCE_BID,
        dailyCap: SYNTHETIC_PREFERENCE_DAILY_CAP,
        monthlyCap: SYNTHETIC_PREFERENCE_MONTHLY_CAP,
        isActive: true,
      });
      console.log(`[DEV Injection] Added synthetic preference for ${lead.leadTypeId} to agent ${agent.id} (${leadTypeBypassReason})`);
    } else {
      // Force existing preference to be active
      injected.leadPreferences = injected.leadPreferences.map(pref => {
        if (pref.leadTypeId === lead.leadTypeId) {
          return {
            ...pref,
            isActive: true,
            bidValue: pref.bidValue > 0 ? pref.bidValue : SYNTHETIC_PREFERENCE_BID,
          };
        }
        return pref;
      });
    }
    
    // If forceAcceptAllLeadTypes, also force all preferences active
    if (effectiveOverride.forceAcceptAllLeadTypes || globalOverrides?.forceAllAgentsEligible) {
      injected.leadPreferences = injected.leadPreferences.map(pref => ({
        ...pref,
        isActive: true,
        bidValue: pref.bidValue > 0 ? pref.bidValue : SYNTHETIC_PREFERENCE_BID,
      }));
    }
  }
  // If not bypassed, agent must pass lead type check naturally

  // --- LICENSED STATE OVERRIDE ---
  // Only bypass state check if explicitly configured
  let shouldBypassState = false;
  let stateBypassReason = '';
  
  if (globalOverrides?.forceLicenses?.includes(lead.state)) {
    shouldBypassState = true;
    stateBypassReason = `Global forceLicenses includes ${lead.state}`;
  } else if (effectiveOverride.overrideLicensedStates.includes(lead.state)) {
    shouldBypassState = true;
    stateBypassReason = `State ${lead.state} in overrideLicensedStates`;
  }

  if (shouldBypassState) {
    attribution.licensedState = { bypassed: true, reason: stateBypassReason };
    
    // Add the state if not already licensed
    if (!injected.licensedStates.includes(lead.state)) {
      injected.licensedStates = [...injected.licensedStates, lead.state];
      console.log(`[DEV Injection] Added license for ${lead.state} to agent ${agent.id} (${stateBypassReason})`);
    }
  }
  // If not bypassed, agent must be licensed in state naturally

  // --- CONTRACT CAPS OVERRIDE ---
  // Only bypass caps if explicitly configured
  if (effectiveOverride.ignoreContractCaps) {
    attribution.contractCaps = { bypassed: true, reason: 'ignoreContractCaps enabled' };
    // Caps are handled via EligibilityContext.agentsIgnoringCaps in the router
    // We reset counts here to ensure the agent passes the daily/monthly cap checks
    injected.leadsReceivedToday = 0;
    injected.leadsReceivedThisMonth = 0;
    console.log(`[DEV Injection] Ignoring caps for agent ${agent.id}`);
  }
  // If not bypassed, agent must pass cap checks naturally

  // --- PRICING RULES OVERRIDE ---
  // Only bypass pricing if explicitly configured
  if (effectiveOverride.ignorePricingRules) {
    attribution.pricingRules = { bypassed: true, reason: 'ignorePricingRules enabled' };
    // Pricing is handled via EligibilityContext.agentsIgnoringPricing in the router
    console.log(`[DEV Injection] Ignoring pricing rules for agent ${agent.id}`);
  }
  // If not bypassed, agent must pass pricing check naturally

  // --- WALLET OVERRIDE ---
  // Only bypass wallet if global override is set
  if (globalOverrides?.forceWalletBalance !== undefined) {
    attribution.wallet = { bypassed: true, reason: `Global wallet forced to ${globalOverrides.forceWalletBalance}` };
    injected.walletBalance = globalOverrides.forceWalletBalance;
  }
  // If not bypassed, agent must have sufficient wallet balance naturally

  return injected;
}

/**
 * Check if an agent is compatible with a lead's vertical.
 * This is the ONLY check that is NEVER bypassed by DEV injection.
 */
function isVerticalCompatible(agent: MockAgent, lead: IncomingLead): boolean {
  return agent.vertical === lead.vertical;
}

/**
 * Resolve the eligible agent pool with DEV injection if applicable.
 * 
 * CRITICAL: This function applies PARTIAL overrides only.
 * Each agent is modified according to their specific override configuration.
 * Agents still need to pass non-overridden eligibility checks naturally.
 */
export function resolveEligibleAgentsWithDevInjection(
  lead: IncomingLead,
  allAgents: MockAgent[],
  options?: {
    /** Use persisted routing context instead of current session state */
    usePersistedContext?: boolean;
    persistedContext?: RoutingContextSnapshot;
  }
): EligibilityInjectionResult {
  // Default: no injection
  const defaultResult: EligibilityInjectionResult = {
    agents: allAgents,
    metadata: {
      eligibilitySource: 'REAL',
      devInjectedAgentIds: [],
    },
  };
  
  // Determine which source of injectable IDs to use
  let injectableIds: string[];
  let usePersistedContext = false;
  
  if (options?.usePersistedContext && options.persistedContext) {
    // Use persisted context (for retries)
    if (!isDevInjectionAvailableFromContext(options.persistedContext)) {
      return defaultResult;
    }
    injectableIds = getInjectableAgentIdsFromContext(options.persistedContext);
    usePersistedContext = true;
  } else {
    // Use current session state
    if (!isDevInjectionAvailable()) {
      return defaultResult;
    }
    injectableIds = getInjectableAgentIds();
  }
  
  // No injectable agents
  if (injectableIds.length === 0) {
    return defaultResult;
  }
  
  // Get per-agent overrides (from session or persisted context)
  let perAgentOverrides: Map<string, PerAgentOverrides>;
  
  if (usePersistedContext && options?.persistedContext?.perAgentOverrides) {
    // Reconstruct map from persisted snapshot
    perAgentOverrides = new Map();
    for (const [agentId, overrides] of Object.entries(options.persistedContext.perAgentOverrides.agentOverrides)) {
      perAgentOverrides.set(agentId, overrides);
    }
  } else if (arePerAgentOverridesActive()) {
    perAgentOverrides = getAllPerAgentOverrides();
  } else {
    perAgentOverrides = new Map();
  }
  
  // Get global overrides
  const globalOverrides = usePersistedContext && options?.persistedContext
    ? {
        forceAllAgentsEligible: options.persistedContext.forceAllAgentsEligible,
        forceWalletBalance: options.persistedContext.forceWalletBalance,
        forceLicenses: options.persistedContext.forceLicenses,
      }
    : areDevOverridesActive()
      ? {
          forceAllAgentsEligible: DEV_AGENT_OVERRIDES.forceAllAgentsEligible,
          forceWalletBalance: DEV_AGENT_OVERRIDES.forceWalletBalance,
          forceLicenses: DEV_AGENT_OVERRIDES.forceLicenses,
        }
      : undefined;
  
  const injectedAgentIds: string[] = [];
  const injectedAgents: MockAgent[] = [];
  const agentAttributions: Record<string, DevOverrideAttribution> = {};
  
  // Determine which agents to inject
  const injectAll = injectableIds.includes('__ALL__');
  
  for (const agent of allAgents) {
    const shouldInject = injectAll || injectableIds.includes(agent.id);
    
    if (shouldInject && isVerticalCompatible(agent, lead)) {
      const override = perAgentOverrides.get(agent.id);
      const injected = createInjectedAgent(agent, lead, override, globalOverrides);
      injectedAgents.push(injected);
      injectedAgentIds.push(agent.id);
      agentAttributions[agent.id] = injected._devOverrideAttribution;
    }
  }
  
  // If we have injected agents, return them
  if (injectedAgents.length > 0) {
    const reason = usePersistedContext 
      ? `DEV overrides from persisted context (${injectedAgentIds.length} agent(s))`
      : `DEV overrides enabled for ${injectedAgentIds.length} agent(s)`;
    
    // Log attribution summary
    console.log(`[DEV Injection] Injected ${injectedAgents.length} agent(s) for lead ${lead.id}:`);
    for (const [agentId, attr] of Object.entries(agentAttributions)) {
      const bypassed = Object.entries(attr)
        .filter(([, v]) => v.bypassed)
        .map(([k]) => k);
      const enforced = Object.entries(attr)
        .filter(([, v]) => !v.bypassed)
        .map(([k]) => k);
      console.log(`  Agent ${agentId}: bypassed=[${bypassed.join(', ')}], enforced=[${enforced.join(', ')}]`);
    }
    
    return {
      agents: injectedAgents,
      metadata: {
        eligibilitySource: 'DEV_INJECTED',
        devInjectedAgentIds: injectedAgentIds,
        injectionReason: reason,
        agentAttributions,
      },
    };
  }
  
  // Fallback: no compatible agents could be injected
  console.warn('[DEV Injection] No vertical-compatible agents found for injection', {
    leadVertical: lead.vertical,
    injectableCount: injectableIds.length,
    agentsChecked: allAgents.length,
    agentVerticals: allAgents.map(a => ({ id: a.id, vertical: a.vertical })),
  });
  return defaultResult;
}

/**
 * Get a summary of DEV injection status for display.
 */
export function getDevInjectionSummary(): {
  available: boolean;
  injectableCount: number;
  reason: string;
} {
  if (!isDevMode) {
    return {
      available: false,
      injectableCount: 0,
      reason: 'Not in development mode',
    };
  }
  
  if (!isDevInjectionAvailable()) {
    return {
      available: false,
      injectableCount: 0,
      reason: 'No DEV overrides configured',
    };
  }
  
  const ids = getInjectableAgentIds();
  const isAll = ids.includes('__ALL__');
  
  return {
    available: true,
    injectableCount: isAll ? -1 : ids.length, // -1 means all
    reason: isAll 
      ? 'All agents injectable (global override)' 
      : `${ids.length} agent(s) with DEV overrides`,
  };
}

/**
 * Check if an agent was DEV-injected.
 */
export function isDevInjectedAgent(agent: MockAgent): agent is InjectedAgent {
  return '_devInjected' in agent && (agent as InjectedAgent)._devInjected === true;
}

/**
 * Get the override attribution for an injected agent.
 */
export function getAgentOverrideAttribution(agent: MockAgent): DevOverrideAttribution | undefined {
  if (isDevInjectedAgent(agent)) {
    return agent._devOverrideAttribution;
  }
  return undefined;
}
